/**
 * The sitting's work: whatever `GET /child/next` hands her, one screen at a
 * time. The surface never decides what comes next; `compose` does. A test
 * starts only at an item boundary, so this loop only asks for the next thing
 * when the current one is done.
 */
import { useCallback, useEffect, useState } from "react";

import { type ChildNext, childApi } from "./api";
import { type LadderState, PracticeItem, TestItem, newLadder } from "./Item";
import { Baigta, Review } from "./Review";
import { useChild, useKeys } from "./ctx";

type Shown = { next: ChildNext; attempt: number; ladder: LadderState | null; reviewOpen: boolean };

export function Work({ onFinished }: { onFinished: () => void }) {
  const ctx = useChild();
  const [shown, setShown] = useState<Shown | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(
    async (retry: LadderState | null = null, prevAttempt = 0) => {
      setError("");
      try {
        await ctx.queue.flush();
        const n = await childApi.next();
        let attempt = 1;
        let ladder: LadderState | null = null;
        if (n.kind === "item" && n.item && n.mode === "practice") {
          if (retry) {
            attempt = n.item.attempt ?? prevAttempt + 1;
            ladder = retry;
          } else {
            attempt = n.item.attempt; // the item.shown's, served with it (errata E-client-1)
            ladder = newLadder(n.item.handle);
          }
        }
        setShown({ next: n, attempt, ladder, reviewOpen: false });
      } catch (e) {
        if (!ctx.lost(e)) setError("Nepavyko gauti užduoties.");
      }
    },
    [ctx],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const retryKeys = { Enter: () => void load() };
  useKeys(retryKeys, !!error || shown?.next.kind === "wait" || shown?.next.kind === "test_intro");

  if (error) {
    return (
      <div>
        <p className="lead">{error}</p>
        <div className="actions">
          <button type="button" className="btn" onClick={() => void load()}>
            Bandyti dar kartą
          </button>
        </div>
      </div>
    );
  }
  if (!shown) return <p className="note">…</p>;
  const n = shown.next;

  if (n.kind === "item" && n.item) {
    if (n.mode === "test") return <TestItem key={n.item.handle} item={n.item} onNext={() => void load()} />;
    return (
      <PracticeItem
        key={n.item.handle}
        item={n.item}
        attempt={shown.attempt}
        ladder={shown.ladder ?? newLadder(n.item.handle)}
        onNext={() => void load()}
        onRetry={(l) => {
          void load(l, shown.attempt);
        }}
      />
    );
  }
  if (n.kind === "test_intro") {
    // No count and no time (§1.2 INTRO).
    return (
      <div data-phase="INTRO">
        <p className="prompt">Dabar keli klausimai. Atsakymų nerodysiu, kol baigsi. Jei nežinai, spausk „Praleisti“.</p>
        <div className="actions">
          <button type="button" className="btn btn-solid" onClick={() => void load()} data-act="next">
            Pradėti <span className="key-hint">Enter</span>
          </button>
        </div>
      </div>
    );
  }
  if (n.kind === "review" && n.test) {
    const test = n.test;
    const empty = !n.review || n.review.length === 0;
    if (!shown.reviewOpen) {
      return (
        <Baigta
          onGo={() => {
            if (!empty) {
              setShown({ ...shown, reviewOpen: true });
              return;
            }
            // Nothing to review: she got every one. The review closes as seen.
            ctx.emit("test.review_closed", { test, by: "done", open_ms: 0 });
            void load();
          }}
        />
      );
    }
    return <Review test={test} items={n.review ?? []} onDone={() => void load()} />;
  }
  if (n.kind === "wait") {
    return (
      <div data-phase="WAIT">
        <p className="lead">Kol kas naujų užduočių nėra.</p>
        <div className="actions">
          <button type="button" className="btn" onClick={() => void load()}>
            Pažiūrėti dar kartą <span className="key-hint">Enter</span>
          </button>
        </div>
      </div>
    );
  }
  if (n.kind === "tool") {
    return (
      <div data-phase="TOOL">
        <p className="lead">Dabar laikas treniruoklei. Ji atsidarys atskirai.</p>
      </div>
    );
  }
  return <EndOfWork onFinished={onFinished} />;
}

function EndOfWork({ onFinished }: { onFinished: () => void }) {
  useKeys({ Enter: onFinished });
  return (
    <div data-phase="END">
      <p className="prompt">Šiam kartui tiek.</p>
      <div className="actions">
        <button type="button" className="btn btn-solid" onClick={onFinished} data-act="finish">
          Baigti <span className="key-hint">Enter</span>
        </button>
      </div>
    </div>
  );
}
